import { ChartEvent, ChartOptions, LegendItem, TooltipItem } from 'chart.js';
import { Table } from '@tanstack/react-table';
import { OrderEntryTableRow } from '../aggregated-order-table/columns';
import { SegmentClickHandler, LabelClickHandler } from './types';
import { areAllSubRowsSelected, getRecursiveSelection } from './utils';
import { ChakraColor } from 'src/utils/color-util';
import { AggregatedOrderViewType } from 'src/gql/graphql';

const DOUBLE_CLICK_DELAY = 300; // ms

// Base chart options that are common to all charts
export const getBaseChartOptions = (): Partial<ChartOptions<any>> => ({
  responsive: true,
  maintainAspectRatio: false,
});

// Common tooltip configuration
export const getTooltipConfig = (viewType: AggregatedOrderViewType) => {
  // Helper for basic label generation
  const getBasicLabel = (context: TooltipItem<any>) => {
    let label = context.dataset.label || '';
    if (label) {
      label += ': ';
    }
    const value =
      viewType === AggregatedOrderViewType.HorizontalBarChart ||
      viewType === AggregatedOrderViewType.StackedHorizontalBarChart
        ? context.parsed.x
        : context.parsed.y;
    if (value !== null) {
      label += value;
    }
    return label;
  };

  // Helper for stacked/line chart label generation
  const getStackedOrLineLabel = (context: TooltipItem<any>) => {
    let label = context.dataset.label || '';

    if (label) {
      label += ': ';
    }
    const value =
      viewType === AggregatedOrderViewType.HorizontalBarChart ||
      viewType === AggregatedOrderViewType.StackedHorizontalBarChart
        ? context.parsed.x
        : context.parsed.y;
    if (value !== null) {
      label += value;
    }
    return label;
  };

  return {
    displayColors: true,
    callbacks: {
      label: function (context: TooltipItem<any>) {
        if (viewType === AggregatedOrderViewType.DoughnutChart) {
          const label = context.label || '';
          const value = context.parsed;
          const dataset = context.dataset;
          const total = dataset.data.reduce(
            (acc: number, curr: number) => acc + curr,
            0
          );
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ${value} (${percentage}%)`;
        } else if (
          viewType === AggregatedOrderViewType.StackedBarChart ||
          viewType === AggregatedOrderViewType.StackedHorizontalBarChart ||
          viewType === AggregatedOrderViewType.LineChart
        ) {
          return getStackedOrLineLabel(context);
        }
        return getBasicLabel(context);
      },
    },
  };
};

// Common interaction configuration
export const getInteractionConfig = (viewType: AggregatedOrderViewType) => ({
  mode:
    viewType === AggregatedOrderViewType.DoughnutChart
      ? ('nearest' as const)
      : ('point' as const),
  intersect: true,
});

// Helper function for simple click handling
const handleSimpleClick = (
  activeElements: any[],
  table: Table<OrderEntryTableRow>,
  setRowSelection: React.Dispatch<React.SetStateAction<any>>,
  shiftPressed: boolean
) => {
  const clickedElement = activeElements[0];
  const dataIndex = clickedElement.index;
  const tableRows = table.getRowModel().rows;

  if (dataIndex < tableRows.length) {
    const clickedRow = tableRows[dataIndex];
    const selectedRowIds = table.getState().rowSelection;

    if (shiftPressed) {
      const isCurrentlySelected = selectedRowIds[clickedRow.id] ?? false;
      if (isCurrentlySelected) {
        // Remove the parent and all its sub-rows
        const entriesToRemove = Object.fromEntries(
          getRecursiveSelection(clickedRow).map(([id]) => [id, false])
        );
        setRowSelection((prev: any) => ({ ...prev, ...entriesToRemove }));
      } else {
        // Add the parent and all its sub-rows
        const entriesToAdd = Object.fromEntries(
          getRecursiveSelection(clickedRow)
        );
        setRowSelection((prev: any) => ({ ...prev, ...entriesToAdd }));
      }
    } else {
      // Select only the parent and all its sub-rows (clear other selections)
      const entries = Object.fromEntries(getRecursiveSelection(clickedRow));
      setRowSelection(entries);
    }
  }
};

// Helper function to select all rows and their sub-rows
const selectAllRowsAndSubRows = (
  table: Table<OrderEntryTableRow>,
  setRowSelection: React.Dispatch<React.SetStateAction<any>>
) => {
  const allRootRows = table.getRowModel().rows;
  let allEntriesToSelect: Record<string, boolean> = {};

  allRootRows.forEach((rootRow) => {
    const recursiveSelectionTuples = getRecursiveSelection(rootRow);
    recursiveSelectionTuples.forEach(([id]) => {
      allEntriesToSelect[id] = true;
    });
  });
  setRowSelection(allEntriesToSelect);
};

// Helper to handle chart-wide double click to select all
const handleChartDoubleClick = (
  chart: any, // Chart.js chart instance
  table: Table<OrderEntryTableRow>,
  setRowSelection: React.Dispatch<React.SetStateAction<any>>,
  currentTime: number
): boolean => {
  if (!chart) return false; // Should not happen if called correctly

  const lastClickTime = chart.lastClickTime || 0;
  if (currentTime - lastClickTime < DOUBLE_CLICK_DELAY) {
    // Double click detected anywhere on the chart
    selectAllRowsAndSubRows(table, setRowSelection);
    chart.lastClickTime = 0; // Reset timer
    return true; // Double-click handled
  }
  chart.lastClickTime = currentTime; // Record current click time
  return false; // Not a double-click or first click of a pair
};

// Common onClick handler factory
export const createOnClickHandler = (
  viewType: AggregatedOrderViewType,
  table: Table<OrderEntryTableRow>,
  setRowSelection: React.Dispatch<React.SetStateAction<any>>,
  handleSegmentClick?: SegmentClickHandler,
  handleLabelClick?: LabelClickHandler
) => {
  return (event: any, activeElements: any[], chart: any) => {
    const shiftPressed = (event.native as MouseEvent)?.shiftKey;
    const currentTime = new Date().getTime();

    if (handleChartDoubleClick(chart, table, setRowSelection, currentTime)) {
      return; // Double-click handled, no further action.
    }

    // Original single-click logic (proceeds if not a double-click)
    if (activeElements.length > 0 && chart) {
      if (
        handleSegmentClick &&
        (viewType === AggregatedOrderViewType.StackedBarChart ||
          viewType === AggregatedOrderViewType.StackedHorizontalBarChart ||
          viewType === AggregatedOrderViewType.LineChart)
      ) {
        // Use advanced segment click for stacked charts and line charts
        handleSegmentClick(
          activeElements,
          chart,
          table,
          setRowSelection,
          shiftPressed
        );
      } else {
        // Simple click handling for basic charts
        handleSimpleClick(activeElements, table, setRowSelection, shiftPressed);
      }
    } else if (
      handleLabelClick &&
      viewType !== AggregatedOrderViewType.DoughnutChart
    ) {
      // Handle label clicks for charts that support it
      handleLabelClick(event, chart, table, setRowSelection, shiftPressed);
    }
  };
};

// Scale configuration factory
export const createScalesConfig = (
  viewType: AggregatedOrderViewType,
  borderColor: string,
  textColor: string,
  table: Table<OrderEntryTableRow>,
  rowSelection: any
) => {
  const isHorizontal =
    viewType === AggregatedOrderViewType.HorizontalBarChart ||
    viewType === AggregatedOrderViewType.StackedHorizontalBarChart;
  const isStacked =
    viewType === AggregatedOrderViewType.StackedBarChart ||
    viewType === AggregatedOrderViewType.StackedHorizontalBarChart;

  if (viewType === AggregatedOrderViewType.DoughnutChart) {
    return undefined; // Doughnut charts don't use scales
  }

  const baseScales = {
    x: {
      stacked: isStacked,
      beginAtZero: isHorizontal,
      position: isHorizontal ? 'top' : 'left',
      grid: {
        display: isHorizontal,
        color: isHorizontal ? borderColor : false,
      },
      ticks: {
        color: isHorizontal
          ? ChakraColor.gray500
          : (context: any) => {
              const dataIndex = context.index;
              const tableRows = table.getRowModel().rows;

              if (dataIndex < tableRows.length) {
                const parentRow = tableRows[dataIndex];
                const allSubRowsSelected = areAllSubRowsSelected(
                  parentRow,
                  rowSelection
                );
                return allSubRowsSelected ? textColor : ChakraColor.gray500;
              }

              return ChakraColor.gray500;
            },
      },
    },
    y: {
      stacked: isStacked,
      beginAtZero: !isHorizontal,
      grid: {
        display: !isHorizontal,
        color: !isHorizontal ? borderColor : false,
      },
      ticks: {
        color: isHorizontal
          ? (context: any) => {
              const dataIndex = context.index;
              const tableRows = table.getRowModel().rows;

              if (dataIndex < tableRows.length) {
                const parentRow = tableRows[dataIndex];
                const allSubRowsSelected = areAllSubRowsSelected(
                  parentRow,
                  rowSelection
                );
                return allSubRowsSelected ? textColor : ChakraColor.gray500;
              }

              return ChakraColor.gray500;
            }
          : ChakraColor.gray500,
      },
    },
  };

  if (isHorizontal) {
    return {
      ...baseScales,
      indexAxis: 'y' as const,
    };
  }

  return baseScales;
};

// Legend configuration for doughnut charts
export const getDoughnutLegendConfig = (
  table: Table<OrderEntryTableRow>,
  setRowSelection: React.Dispatch<React.SetStateAction<any>>
) => ({
  onClick: (event: ChartEvent, legendItem: LegendItem) => {
    const shiftPressed = (event.native as MouseEvent)?.shiftKey || false;
    const dataIndex = legendItem.index;

    if (typeof dataIndex !== 'undefined') {
      const syntheticActiveElement = { index: dataIndex };
      handleSimpleClick(
        [syntheticActiveElement],
        table,
        setRowSelection,
        shiftPressed
      );
    }
  },
  position: 'right' as const,
  labels: {
    padding: 15,
    usePointStyle: true,
    font: {
      size: 12,
    },
  },
});

// Complete chart options factory
export const createChartOptions = (
  viewType: AggregatedOrderViewType,
  borderColor: string,
  textColor: string,
  table: Table<OrderEntryTableRow>,
  rowSelection: any,
  setRowSelection: React.Dispatch<React.SetStateAction<any>>,
  handleSegmentClick?: SegmentClickHandler,
  handleLabelClick?: LabelClickHandler
): ChartOptions<'bar'> | ChartOptions<'doughnut'> | ChartOptions<'line'> => {
  const baseOptions = getBaseChartOptions();
  const interaction = getInteractionConfig(viewType);
  const tooltip = getTooltipConfig(viewType);
  const onClick = createOnClickHandler(
    viewType,
    table,
    setRowSelection,
    handleSegmentClick,
    handleLabelClick
  );
  const scales = createScalesConfig(
    viewType,
    borderColor,
    textColor,
    table,
    rowSelection
  );

  const plugins: any = {
    tooltip,
  };

  if (viewType === AggregatedOrderViewType.DoughnutChart) {
    plugins.legend = getDoughnutLegendConfig(table, setRowSelection);
  } else {
    // Hide legend for all other chart types
    plugins.legend = {
      display: false,
    };
  }

  const options: ChartOptions<any> = {
    ...baseOptions,
    interaction,
    plugins,
    onClick,
  };

  if (scales) {
    if ('indexAxis' in scales) {
      options.indexAxis = scales.indexAxis;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { indexAxis, ...scalesWithoutIndexAxis } = scales;
      options.scales = scalesWithoutIndexAxis;
    } else {
      options.scales = scales;
    }
  }

  // Return with proper type based on viewType
  if (viewType === AggregatedOrderViewType.DoughnutChart) {
    return options as ChartOptions<'doughnut'>;
  } else if (viewType === AggregatedOrderViewType.LineChart) {
    return options as ChartOptions<'line'>;
  } else {
    return options as ChartOptions<'bar'>;
  }
};
